import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { generateEventId, trackMetaLead } from '../lib/metaTracking';
import { imageUrl } from '../lib/images';

export default function JobPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Application form
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    position: 'Chauffør til 8-bils autotransporter',
    message: '',
    website_url: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const eventIdRef = useRef(null);

  useEffect(() => {
    fetch('/_api/public/content/jobs')
      .then((res) => res.json())
      .then((data) => {
        const items = Array.isArray(data) ? data : data.items || [];
        setJobs(items);
        setLoading(false);
      })
      .catch((err) => {
        console.error('Fejl ved hentning af jobs:', err);
        setLoading(false);
      });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setErrorMsg('');

    if (!eventIdRef.current) {
      eventIdRef.current = generateEventId();
    }
    const currentEventId = eventIdRef.current;

    try {
      const payload = {
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        message: `Jobansøgning til: ${formData.position}\n\nBesked / Erfaring:\n${formData.message}`,
        website_url: formData.website_url,
        job_position: formData.position
      };

      const res = await fetch('/_api/webform/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!res.ok) throw new Error('Kunne ikke indsende ansøgningen. Prøv venligst igen.');

      await trackMetaLead({
        eventId: currentEventId,
        email: formData.email,
        phone: formData.phone,
        contentName: `Jobansøgning: ${formData.position}`
      });

      eventIdRef.current = null;
      setSubmitted(true);
      setFormData({
        name: '',
        phone: '',
        email: '',
        position: 'Chauffør til 8-bils autotransporter',
        message: '',
        website_url: ''
      });
    } catch (err) {
      setErrorMsg(err.message || 'Der opstod en fejl. Ring direkte til Martin på 70 700 800.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEOHead
        title="Chauffør søges | Bliv chauffør hos MC Transport"
        description="Vi søger chauffører til 8-bils autotransporter, vejhjælp og bjærgning. Bliv en del af vores familievirksomhed med 3F overenskomst og gode kolleger."
      />

      {/* Hero */}
      <section className="bg-zinc-950 text-white py-16 lg:py-24 border-b border-zinc-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(220,38,38,0.2),transparent_70%)]" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-red-500 font-condensed mb-2 block">
            Karriere hos MC Transport
          </span>
          <h1 className="font-condensed text-4xl sm:text-6xl font-extrabold uppercase tracking-tight text-white">
            Chauffør søges
          </h1>
          <p className="mt-4 text-base sm:text-xl text-zinc-300 max-w-2xl mx-auto">
            Vil du være en del af et stærkt team hos <Link to="/" className="font-semibold text-white hover:text-red-400 underline decoration-red-500 underline-offset-2 transition-colors">MC Transport</Link> i en familieejet virksomhed i vækst? Vi søger dygtige kolleger, der vil være med på rejsen.
          </p>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-4">
            <a
              href="tel:+4570700800"
              className="px-6 py-3.5 bg-red-600 hover:bg-red-700 text-white font-condensed text-xl font-bold uppercase rounded shadow"
            >
              <i className="fa-solid fa-phone mr-2" aria-hidden="true" />
              <span>Ring til Martin: +45 70 700 800</span>
            </a>
            <a
              href="#ansoeg-nu"
              className="px-6 py-3.5 bg-zinc-900 hover:bg-zinc-800 text-white font-condensed text-xl font-bold uppercase rounded border border-white/20"
            >
              Send ansøgning online
            </a>
          </div>
        </div>
      </section>

      {/* Authentic Scrape Description Block */}
      <section className="py-16 bg-white border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 sm:p-10 space-y-6">
            <div className="flex items-center gap-4 border-b border-zinc-200 pb-6">
              <img
                src={imageUrl('https://assets.auxodev.dk/assets/7ab2fd9d-1ad5-42a7-a15d-22229bea7818/martin-indehaver-mc-transport-kontor.jpg', 'width=160,height=160,fit=cover,quality=90,format=auto')}
                alt="Martin, indehaver af MC Transport"
                className="w-20 h-20 rounded-full object-cover border-2 border-red-600 shrink-0 shadow"
              />
              <div>
                <h2 className="font-condensed text-2xl font-bold uppercase text-zinc-900">
                  En arbejdsplads, hvor du bliver set
                </h2>
                <p className="text-xs text-zinc-600 font-medium">
                  Personlig kontakt: Martin (Indehaver) · Tlf: 70 700 800 · Mail: job@mcautotransport.dk
                </p>
              </div>
            </div>

            {/* Verbatim text from scrape */}
            <div className="space-y-4 text-zinc-700 text-sm sm:text-base leading-relaxed">
              <div className="rounded-xl overflow-hidden border border-zinc-200 shadow-sm my-4">
                <img
                  src={imageUrl('/_assets/scania-lastbil-blokvogn-transport.jpg', 'width=1000,quality=85,format=auto')}
                  alt="Chauffør hos MC Transport med tommelfinger op ved rød Scania lastbil"
                  className="w-full h-64 sm:h-80 object-cover"
                />
              </div>

              <p>
                <strong>Annoncer:</strong><br />
                Hvis du gerne vil køre <Link to="/autohjaelp" className="text-red-600 font-semibold hover:underline">vejhjælp</Link> eller <Link to="/ydelser/autotransport-ydelser" className="text-red-600 font-semibold hover:underline">autotransport</Link> er du altid velkommen til at sende en uopfordret ansøgning. Vi har biler over og under 3.500 kg som kører vejhjælp.
              </p>
              <p>
                <strong>Vi Søger lastbilchauffør:</strong><br />
                Vi søger en ny kollega til vores 6 biltransport og 2 chauffør til 8 biltransport.
              </p>
              <p>
                Om du er mand, kvinde, ung, gammel eller alt der imellem, søger vi dig der har lyst til og være en del af vores MC Transport familie.
              </p>
              <p>
                Vi går meget op i sammenhold og hygge samtidig med at vi selvfølgelig forventer at der sker noget når vi er på arbejde, at vi hjælper hinanden og spørger hvis der er noget der driller.
              </p>
              <p>
                Vi er en familie drevet virksomhed beliggende i <Link to="/kontakt" className="text-zinc-900 font-medium hover:text-red-600 underline decoration-zinc-300 underline-offset-2">Ugerløse ved Holbæk</Link>. Martin er chefen, Pia (Martins bedre halvdel) sidder med regnskab og lønninger når hun er tilbage fra barsel. Vi har 3 unger som er og finde på kontoret i tide og utide, til stor glæde for vores 2 kontoransatte og nyansatte disponent og resten af flokken af chauffører når de omlæsser eller holder der for natten. Læs mere <Link to="/om-os" className="text-red-600 font-semibold hover:underline">om vores virksomhed her</Link>.
              </p>
              <p>
                Erfaring tæller jo altid som et plus, men vi ved også at dette fag er specielt og vi er derfor med på at lære dig op i at læsse nye, brugte og skadet biler.
              </p>
              <p>
                Du skal have lyst til at sove i lastbilen fra mandag til fredag. Du vil få løn, pension og sundhedsforsikring ifølge overenskomsten (3F).
              </p>
              <p>
                <strong>Start hurtigst muligt.</strong><br />
                Send ansøgning på: <a href="mailto:job@mcautotransport.dk" className="text-red-600 font-bold hover:underline">job@mcautotransport.dk</a>. Vi glæder os til at høre fra dig.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Open Positions List */}
      <section className="py-16 bg-zinc-100 border-b border-zinc-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
              Ledige Stillinger
            </span>
            <h2 className="font-condensed text-3xl sm:text-4xl font-extrabold uppercase tracking-tight text-zinc-950 mt-1">
              Aktuelle stillinger hos MC Transport
            </h2>
          </div>

          {loading ? (
            <div className="text-center py-12 text-zinc-500 font-mono text-sm">
              <i className="fa-solid fa-spinner fa-spin text-2xl text-red-600 mb-2" aria-hidden="true" />
              <p>Henter jobopslag...</p>
            </div>
          ) : (
            <div className="space-y-6">
              {jobs.map((job) => (
                <div
                  key={job.id}
                  className="bg-white rounded-xl p-6 sm:p-8 border border-zinc-200 shadow-sm hover:shadow-md transition-shadow"
                >
                  <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 border-b border-zinc-100 pb-4 mb-4">
                    <div>
                      <h3 className="font-condensed text-2xl font-bold uppercase text-zinc-900">
                        {job.title}
                      </h3>
                      {job.terms_info && (
                        <p className="text-xs text-red-600 font-semibold mt-1">
                          <i className="fa-solid fa-check-double mr-1.5" aria-hidden="true" />
                          {job.terms_info}
                        </p>
                      )}
                    </div>
                    <a
                      href="#ansoeg-nu"
                      onClick={() => setFormData((prev) => ({ ...prev, position: job.title }))}
                      className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white font-condensed text-sm font-bold uppercase rounded shadow shrink-0 text-center"
                    >
                      Søg denne stilling
                    </a>
                  </div>

                  <div
                    className="prose prose-zinc max-w-none text-zinc-700 text-xs sm:text-sm leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: job.description }}
                  />

                  <div className="mt-4 pt-3 border-t border-zinc-100 flex flex-wrap items-center gap-4 text-xs text-zinc-500 font-medium">
                    {job.contact_person && (
                      <span>
                        <strong className="text-zinc-700">Kontaktperson: </strong> {job.contact_person}
                      </span>
                    )}
                    {job.contact_phone && (
                      <span>
                        <strong className="text-zinc-700">Telefon: </strong>
                        <a
                          href={`tel:${job.contact_phone.startsWith('+') ? job.contact_phone : '+45' + job.contact_phone.replace(/\s+/g, '')}`}
                          className="text-red-600 hover:underline"
                        >
                          {job.contact_phone.startsWith('+') ? job.contact_phone : `+45 ${job.contact_phone}`}
                        </a>
                      </span>
                    )}
                    {job.contact_email && (
                      <span>
                        <strong className="text-zinc-700">Email: </strong>
                        <a href={`mailto:${job.contact_email}`} className="text-red-600 hover:underline">
                          {job.contact_email}
                        </a>
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Application Form */}
      <section id="ansoeg-nu" className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-zinc-50 border border-zinc-300 rounded-2xl p-8 sm:p-10 shadow-lg">
            <div className="text-center mb-6">
              <span className="text-xs font-bold uppercase tracking-wider text-red-600 font-condensed">
                Hurtig Ansøgning
              </span>
              <h3 className="font-condensed text-3xl font-extrabold uppercase text-zinc-950 mt-1">
                Send din ansøgning til MC Transport
              </h3>
              <p className="text-xs sm:text-sm text-zinc-600 mt-2">
                Udfyld formularen, eller send en e-mail direkte til <a href="mailto:job@mcautotransport.dk" className="text-red-600 font-bold underline">job@mcautotransport.dk</a>.
              </p>
            </div>

            {submitted ? (
              <div className="p-6 bg-emerald-50 border border-emerald-300 rounded-lg text-emerald-800 text-center">
                <i className="fa-solid fa-circle-check text-3xl text-emerald-600 mb-2" aria-hidden="true" />
                <h4 className="font-bold text-lg mb-1">Mange tak for din ansøgning!</h4>
                <p className="text-xs sm:text-sm">
                  Vi behandler alle henvendelser fortroligt og vender hurtigt tilbage. Du er også velkommen til at ringe direkte til Martin på 70 700 800.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Honeypot field */}
                <div className="sr-only absolute -left-[9999px]" aria-hidden="true">
                  <label htmlFor="website_url">Hjemmeside url</label>
                  <input
                    type="text"
                    id="website_url"
                    name="website_url"
                    tabIndex={-1}
                    autoComplete="off"
                    value={formData.website_url}
                    onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Stilling der søges *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.position}
                    onChange={(e) => setFormData({ ...formData, position: e.target.value })}
                    className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      Dit Navn *
                    </label>
                    <input
                      type="text"
                      required
                      placeholder="Fornavn og efternavn"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="block text-xs font-semibold text-zinc-700 mb-1">
                      Telefonnummer *
                    </label>
                    <input
                      type="tel"
                      required
                      placeholder="Dit telefonnummer"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    E-mailadresse *
                  </label>
                  <input
                    type="email"
                    required
                    placeholder="din@email.dk"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-zinc-700 mb-1">
                    Kort om dig selv, kørekort (C/CE) og erfaring *
                  </label>
                  <textarea
                    required
                    rows={4}
                    placeholder="Fortæl kort om dine kørekort, EU-efteruddannelse, erfaring og hvornår du kan starte..."
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full text-xs p-3 bg-white border border-zinc-300 rounded focus:border-red-600 focus:outline-none"
                  />
                </div>

                {errorMsg && (
                  <p className="text-xs text-red-600 font-semibold">{errorMsg}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="w-full py-4 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white font-condensed text-xl font-bold uppercase tracking-wider rounded shadow transition-colors"
                >
                  {submitting ? 'Indsender ansøgning...' : 'Send ansøgning nu'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </>
  );
}
